<template>
    <v-dialog v-model="dialog" max-width="600" persistent>
        <template v-slot:activator="{ on }">
            <v-btn color="primary" text v-on="on">Edit</v-btn>
        </template>
        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <div class="d-flex align-center secondary--text">
                    <span>Space Details</span>
                </div>

                <v-spacer></v-spacer>
                <v-btn :disabled="saving" @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-form ref="editForm" v-model="formValid" @submit.prevent="saveSpaceDetails" class="mt-4">
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                ref="spaceNameField"
                                v-model="editedSpaceName"
                                label="Space name"
                                outlined
                                dense
                                :rules="[rules.required]"
                                :disabled="saving"
                                required></v-text-field>

                            <v-textarea
                                v-model="editedSpaceDescription"
                                label="Space description"
                                outlined
                                dense
                                rows="3"
                                auto-grow
                                :disabled="saving"></v-textarea>

                            <v-menu
                                v-if="showArchiveByDate"
                                ref="datePickerMenu"
                                v-model="datePickerMenu"
                                :close-on-content-click="false"
                                :return-value.sync="editedArchiveByDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="editedArchiveByDate"
                                        label="Archive on"
                                        outlined
                                        dense
                                        readonly
                                        :rules="[rules.required]"
                                        :disabled="saving || !canEditArchiveByDate"
                                        v-bind="attrs"
                                        v-on="on"
                                        required></v-text-field>
                                </template>
                                <v-date-picker v-model="editedArchiveByDate" :min="minArchiveByDate" :max="maxArchiveByDate" no-title scrollable>
                                    <v-spacer></v-spacer>
                                    <v-btn text color="secondary" @click="datePickerMenu = false">Cancel</v-btn>
                                    <v-btn text color="primary" @click="$refs.datePickerMenu.save(editedArchiveByDate)">OK</v-btn>
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-form>
                <v-alert v-if="error" type="error" text>Failed to update space details. If the problem persists, contact support.</v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="success" v-if="showSuccess"><v-icon>mdi-check</v-icon></v-btn>
                <v-btn v-else :disabled="!formValid || !hasChanges || saving" :loading="saving" color="primary" text @click="saveSpaceDetails">
                    Save Changes
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'SpaceDetailsEditDialog',
    props: {
        spaceName: {
            type: String,
            default: ''
        },
        spaceDescription: {
            type: String,
            default: ''
        },
        archiveByDate: {
            type: String,
            default: '9999-12-31'
        },
        maxArchiveByDate: {
            type: String,
            default: ''
        },
        minArchiveByDate: {
            type: String,
            default: new Date().toISOString().slice(0, 10)
        },
        showArchiveByDate: {
            type: Boolean,
            default: false
        },
        canEditArchiveByDate: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            dialog: false,
            error: false,
            showSuccess: false,
            saving: false,
            formValid: false,
            editedSpaceName: '',
            editedSpaceDescription: '',
            editedArchiveByDate: '',
            datePickerMenu: false,
            rules: {
                required: v => !!v || 'Field is required'
            }
        }
    },
    computed: {
        hasChanges() {
            const hasDetailsChanges = this.editedSpaceName !== (this.spaceName || '') || this.editedSpaceDescription !== (this.spaceDescription || '')
            const hasArchiveByDateChanges = this.showArchiveByDate && this.editedArchiveByDate !== (this.archiveByDate || '9999-12-31')
            return hasDetailsChanges || hasArchiveByDateChanges
        }
    },
    watch: {
        dialog(newVal) {
            if (newVal) {
                this.initializeForm()
            } else {
                this.resetForm()
            }
        }
    },
    methods: {
        normalizeArchiveByDate(value) {
            const today = new Date().toISOString().slice(0, 10)
            let normalized = value || today

            if (this.minArchiveByDate && normalized < this.minArchiveByDate) {
                normalized = this.minArchiveByDate
            }

            // If existing value is beyond allowed max, clamp so the calendar opens on a selectable date.
            if (this.maxArchiveByDate && normalized > this.maxArchiveByDate) {
                normalized = this.maxArchiveByDate
            }

            return normalized
        },
        initializeForm() {
            this.editedSpaceName = this.spaceName || ''
            this.editedSpaceDescription = this.spaceDescription || ''
            this.editedArchiveByDate = this.normalizeArchiveByDate(this.archiveByDate || '9999-12-31')
            this.error = false
            this.showSuccess = false

            this.$nextTick(() => {
                if (this.$refs.editForm) {
                    this.$refs.editForm.resetValidation()
                }
            })
        },
        resetForm() {
            this.editedSpaceName = ''
            this.editedSpaceDescription = ''
            this.editedArchiveByDate = ''
            this.datePickerMenu = false
            this.error = false
            this.showSuccess = false
            this.saving = false
        },
        cancelEdit() {
            this.dialog = false
        },
        async saveSpaceDetails() {
            if (!this.hasChanges) {
                this.dialog = false
                return
            }

            this.saving = true
            this.error = false

            try {
                const patchBody = {}

                if (this.editedSpaceName !== (this.spaceName || '')) {
                    patchBody.long_id = this.editedSpaceName.trim()
                }

                if (this.editedSpaceDescription !== (this.spaceDescription || '')) {
                    patchBody.description = this.editedSpaceDescription
                }

                if (this.showArchiveByDate && this.editedArchiveByDate !== (this.archiveByDate || '9999-12-31')) {
                    patchBody.archive_by_date = this.editedArchiveByDate
                }

                const { data } = await this.$axios.patch(`/spaces/${this.$route.params.sid}`, patchBody)

                this.$emit('updated', data)
                this.showSuccess = true
                setTimeout(() => {
                    this.dialog = false
                }, 1200)
            } catch (error) {
                console.error('Error updating space details:', error)
                this.error = true
            } finally {
                this.saving = false
            }
        }
    }
}
</script>
